import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Sparkles, ShieldCheck, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from './Button';
import {
  getAiStatus,
  getAiVoorbeeld,
  maakAiConcept,
  type AiConcept,
  type AiVoorbeeld,
  type Sjabloon,
} from '../services/gespreksverslagApi';

/**
 * AI bij gespreksverslagen, in twee stappen:
 *  1. Controleren: de server pseudonimiseert de aantekeningen en toont wat er
 *     naar de AI zou gaan. Er wordt dan nog niets verstuurd.
 *  2. Concept maken: na bevestiging gaat alleen die gepseudonimiseerde tekst naar
 *     de AI. Echte namen komen alleen terug in Vak A (voor de medewerker zelf).
 * Zichtbaar zodra de server AI-ondersteuning aan heeft staan (GESPREKSVERSLAG_AI).
 */
interface Props {
  sjabloon: Sjabloon;
  deel5: boolean;
  afdelingen: string[];
  locaties: string[];
  onConcept: (concept: AiConcept) => void;
}

const foutmelding = (e: any) => toast.error(e?.response?.data?.message || 'Er ging iets mis.');

export const GespreksverslagAiPaneel: React.FC<Props> = ({ sjabloon, deel5, afdelingen, locaties, onConcept }) => {
  const { data: status } = useQuery({ queryKey: ['gespreksverslag-ai-status'], queryFn: getAiStatus, staleTime: 5 * 60_000 });
  const [open, setOpen] = useState(false);
  const [aantekeningen, setAantekeningen] = useState('');
  const [extra, setExtra] = useState('');
  const [voorbeeld, setVoorbeeld] = useState<AiVoorbeeld | null>(null);
  const [akkoord, setAkkoord] = useState(false);

  const invoer = () => ({
    aantekeningen,
    sjabloon,
    deel5,
    afdelingen,
    locaties,
    extra_termen: extra.split(',').map((t) => t.trim()).filter(Boolean),
  });

  const controleer = useMutation({
    mutationFn: () => getAiVoorbeeld(invoer()),
    onSuccess: (v) => {
      setVoorbeeld(v);
      setAkkoord(false);
    },
    onError: foutmelding,
  });
  const concept = useMutation({
    mutationFn: () => maakAiConcept(invoer()),
    onSuccess: (c) => {
      onConcept(c);
      toast.success('Concept ingevuld. Controleer en pas het aan voordat u indient.');
    },
    onError: foutmelding,
  });

  if (!status?.beschikbaar) return null;

  // Elke wijziging na het controleren maakt het voorbeeld ongeldig.
  const wijzig = (zet: (v: string) => void) => (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    zet(e.target.value);
    setVoorbeeld(null);
    setAkkoord(false);
  };

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40">
      <button type="button" className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-indigo-900" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Sparkles className="w-4 h-4" aria-hidden />
        Concept maken uit mijn aantekeningen (AI, gepseudonimiseerd)
      </button>
      {open && (
        <div className="space-y-3 px-4 pb-4">
          <div className="flex gap-2 rounded-md bg-white border border-indigo-100 p-2 text-xs text-gray-700">
            <ShieldCheck className="w-4 h-4 shrink-0 text-indigo-600" aria-hidden />
            <span>
              Namen, datums, BSN, telefoonnummers, adressen, afdelingen en locaties worden eerst op de BeLagom-server vervangen door
              plaatshouders. U ziet precies wat er naar de AI gaat voordat u het verstuurt. Aantekeningen en concept worden niet opgeslagen.
              Echte namen komen alleen terug in Vak A; Vak B en het werkvloeradvies blijven zonder personen.
            </span>
          </div>
          <label className="block text-sm">
            <span className="text-gray-700">Uw aantekeningen van het gesprek</span>
            <textarea className="mt-1 w-full rounded-md border-gray-300" rows={6} maxLength={12000} value={aantekeningen} onChange={wijzig(setAantekeningen)} />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">Extra woorden verbergen <span className="text-gray-500">(komma-gescheiden, bijv. een bijnaam of projectnaam)</span></span>
            <input className="mt-1 w-full rounded-md border-gray-300" value={extra} onChange={wijzig(setExtra)} />
          </label>
          <Button type="button" variant="outline" size="sm" onClick={() => controleer.mutate()} loading={controleer.isPending} disabled={!aantekeningen.trim()}>
            1. Controleer wat er verstuurd wordt
          </Button>

          {voorbeeld && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-700">Dit gaat naar de AI ({voorbeeld.vervangingen.length} vervangingen):</p>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-white p-3 text-xs text-gray-800">{voorbeeld.tekst}</pre>
              {voorbeeld.vervangingen.length > 0 && (
                <details className="text-xs text-gray-600">
                  <summary className="cursor-pointer">Vervangingen bekijken (alleen voor u zichtbaar)</summary>
                  <ul className="mt-1 space-y-0.5">
                    {voorbeeld.vervangingen.map((v) => (
                      <li key={v.plaatshouder}><code>{v.plaatshouder}</code> = {v.origineel}</li>
                    ))}
                  </ul>
                </details>
              )}
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input type="checkbox" className="mt-1" checked={akkoord} onChange={(e) => setAkkoord(e.target.checked)} />
                Ik heb gecontroleerd dat hierin niemand herkenbaar is. Staat er toch iets herleidbaars in, pas dan de aantekeningen aan of voeg het toe bij ‘Extra woorden verbergen’.
              </label>
              <Button type="button" size="sm" onClick={() => concept.mutate()} loading={concept.isPending} disabled={!akkoord}>
                <Sparkles className="w-4 h-4 mr-1" aria-hidden />
                2. Concept maken
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GespreksverslagAiPaneel;
